<template>
  <div class="designer-page">
    <div class="designer-container">

      <!-- Progress dots -->
      <div class="progress-dots" v-if="!isComplete">
        <span
          v-for="(q, i) in questions"
          :key="i"
          class="dot"
          :class="{ active: i === currentStep, done: i < currentStep }"
        ></span>
      </div>

      <!-- Question flow -->
      <transition name="fade" mode="out-in">
        <div v-if="!isComplete" :key="currentStep" class="question-block">
          <p class="hint">{{ questions[currentStep].hint }}</p>
          <h2 class="question-text">{{ questions[currentStep].label }}</h2>
          <input
            ref="input"
            type="text"
            class="designer-input"
            :placeholder="questions[currentStep].placeholder"
            v-model="answers[questions[currentStep].key]"
            @keyup.enter="next"
            autofocus
          />
          <div class="nav-buttons">
            <button v-if="currentStep > 0" class="btn-back" @click="prev">Back</button>
            <button
              class="btn-next"
              :disabled="!answers[questions[currentStep].key]"
              @click="next"
            >
              {{ currentStep === questions.length - 1 ? 'Finish' : 'Next' }}
            </button>
          </div>
        </div>
      </transition>

      <!-- Complete state -->
      <transition name="fade">
        <div v-if="isComplete" class="complete-block">
          <h2 class="complete-title">Your game is ready!</h2>
          <div class="spec-card">
            <h3 class="spec-name">{{ answers.name }}</h3>
            <p class="spec-theme">{{ answers.theme }}</p>
            <div class="spec-grid">
              <div class="spec-item">
                <span class="spec-label">Players</span>
                <span class="spec-value">{{ answers.players }}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">Play Time</span>
                <span class="spec-value">{{ answers.duration }}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">Mechanic</span>
                <span class="spec-value">{{ answers.mechanic }}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">Win Condition</span>
                <span class="spec-value">{{ answers.winCondition }}</span>
              </div>
            </div>
            <div class="spec-turn" v-if="answers.turnFlow">
              <span class="spec-label">Turn Flow</span>
              <p class="spec-value">{{ answers.turnFlow }}</p>
            </div>
          </div>
          <div class="complete-actions">
            <button class="btn-save" @click="saveGame" :disabled="isSaving">
              {{ isSaving ? 'Saving...' : 'Save Game' }}
            </button>
            <button class="btn-restart" @click="restart">Start Over</button>
          </div>
          <div v-if="saveError" class="save-error">{{ saveError }}</div>
        </div>
      </transition>

    </div>
  </div>
</template>

<script>
import { getAuth } from 'firebase/auth'
import { collection, addDoc, Timestamp } from 'firebase/firestore'
import { firestore } from '@/firebase'

export default {
  name: 'GameDesignerPage',
  data() {
    return {
      currentStep: 0,
      isComplete: false,
      isSaving: false,
      saveError: null,
      questions: [
        {
          key: 'name',
          label: "What's your game called?",
          hint: "Every great game starts with a name.",
          placeholder: "e.g. Dragon's Hoard"
        },
        {
          key: 'theme',
          label: "What's it about?",
          hint: "The setting or story behind your game.",
          placeholder: "e.g. Pirates racing to find buried treasure"
        },
        {
          key: 'players',
          label: "How many players?",
          hint: "A range works great too.",
          placeholder: "e.g. 2-4"
        },
        {
          key: 'duration',
          label: "How long is a game?",
          hint: "A rough estimate is fine.",
          placeholder: "e.g. 30 minutes"
        },
        {
          key: 'mechanic',
          label: "What's the core mechanic?",
          hint: "The main thing players do on their turn.",
          placeholder: "e.g. Roll dice and move, draw cards, place tiles"
        },
        {
          key: 'winCondition',
          label: "How does someone win?",
          hint: "What are players racing toward?",
          placeholder: "e.g. First to collect 10 gems"
        },
        {
          key: 'turnFlow',
          label: "What happens on a turn?",
          hint: "Walk through a typical turn in a sentence.",
          placeholder: "e.g. Draw a card, play an action, collect resources"
        }
      ],
      answers: {
        name: '',
        theme: '',
        players: '',
        duration: '',
        mechanic: '',
        winCondition: '',
        turnFlow: ''
      }
    }
  },
  methods: {
    next() {
      if (!this.answers[this.questions[this.currentStep].key]) return
      if (this.currentStep < this.questions.length - 1) {
        this.currentStep++
        this.$nextTick(() => {
          if (this.$refs.input) this.$refs.input.focus()
        })
      } else {
        this.isComplete = true
      }
    },
    prev() {
      if (this.currentStep > 0) {
        this.currentStep--
        this.$nextTick(() => {
          if (this.$refs.input) this.$refs.input.focus()
        })
      }
    },
    restart() {
      this.currentStep = 0
      this.isComplete = false
      this.saveError = null
      this.answers = {
        name: '',
        theme: '',
        players: '',
        duration: '',
        mechanic: '',
        winCondition: '',
        turnFlow: ''
      }
    },
    async saveGame() {
      const auth = getAuth()
      const user = auth.currentUser

      if (!user) {
        this.saveError = 'You need to be logged in to save.'
        return
      }

      this.isSaving = true
      this.saveError = null

      try {
        const boardGameData = {
          name: this.answers.name,
          description: `${this.answers.theme}. Core mechanic: ${this.answers.mechanic}. Win by: ${this.answers.winCondition}.`,
          type: 'other',
          minPlayers: parseInt(this.answers.players) || 2,
          // Must match the complexity options in CreateBoardGamePage.vue
          // (beginner | intermediate | advanced | expert). 'medium' is not one
          // of them, and left the dropdown blank when editing the game.
          complexity: 'intermediate',
          theme: this.answers.theme,
          mechanic: this.answers.mechanic,
          winCondition: this.answers.winCondition,
          turnFlow: this.answers.turnFlow,
          duration: this.answers.duration,
          userId: user.uid,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          approved: false,
          source: 'game-designer'
        }

        const ref = await addDoc(collection(firestore, 'boardGames'), boardGameData)

        this.$router.push({
          name: 'BoardGameDetail',
          params: { id: ref.id },
          query: { message: `"${this.answers.name}" created! Start building it out.` }
        })
      } catch (err) {
        console.error('Error saving game:', err)
        this.saveError = 'Something went wrong. Please try again.'
      } finally {
        this.isSaving = false
      }
    }
  }
}
</script>

<style scoped>
.designer-page {
  min-height: calc(100vh - 60px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.designer-container {
  max-width: 560px;
  width: 100%;
}

/* Progress dots */
.progress-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 3rem;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: var(--pencil-light, #d4c9b5);
  transition: all 0.3s ease;
}

.dot.active {
  background-color: var(--pop-orange, #ff6b35);
  transform: scale(1.3);
}

.dot.done {
  background-color: var(--pop-green, #34b872);
}

/* Question block */
.question-block {
  text-align: center;
}

.hint {
  font-family: var(--font-handwritten, 'Caveat', cursive);
  color: var(--ink-muted, #8a8a8a);
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
}

.question-text {
  font-family: var(--font-heading, 'Fredoka', sans-serif);
  color: var(--ink, #2d2d2d);
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 2rem;
}

.designer-input {
  width: 100%;
  padding: 1rem 0;
  font-size: 1.25rem;
  font-family: var(--font-body, 'Inter', sans-serif);
  color: var(--ink, #2d2d2d);
  background: transparent;
  border: none;
  border-bottom: 2px solid var(--pencil-light, #d4c9b5);
  outline: none;
  text-align: center;
  transition: border-color 0.2s ease;
}

.designer-input:focus {
  border-bottom-color: var(--pop-orange, #ff6b35);
}

.designer-input::placeholder {
  color: var(--ink-muted, #8a8a8a);
  font-style: italic;
}

/* Navigation */
.nav-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2.5rem;
}

.btn-next,
.btn-back {
  font-family: var(--font-heading, 'Fredoka', sans-serif);
  font-size: 1rem;
  padding: 0.6rem 2rem;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-next {
  background-color: var(--pop-orange, #ff6b35);
  color: white;
}

.btn-next:hover:not(:disabled) {
  background-color: var(--pop-orange-light, #ff8c5e);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
}

.btn-next:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-back {
  background-color: var(--bg-sketch, #eee8dd);
  color: var(--ink-light, #5a5a5a);
}

.btn-back:hover {
  background-color: var(--pencil-light, #d4c9b5);
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(12px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

/* Complete state */
.complete-block {
  text-align: center;
}

.complete-title {
  font-family: var(--font-heading, 'Fredoka', sans-serif);
  color: var(--pop-green, #34b872);
  font-size: 2rem;
  margin-bottom: 2rem;
}

.spec-card {
  background: var(--bg-surface, #fff);
  border: 1px solid var(--border-sketch, rgba(0,0,0,0.08));
  border-radius: 16px;
  padding: 2rem;
  text-align: left;
  box-shadow: 0 4px 20px var(--shadow-soft, rgba(0,0,0,0.08));
}

.spec-name {
  font-family: var(--font-heading, 'Fredoka', sans-serif);
  font-size: 1.5rem;
  color: var(--ink, #2d2d2d);
  margin-bottom: 0.25rem;
}

.spec-theme {
  font-family: var(--font-handwritten, 'Caveat', cursive);
  color: var(--ink-muted, #8a8a8a);
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
}

.spec-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.spec-item {
  display: flex;
  flex-direction: column;
}

.spec-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ink-muted, #8a8a8a);
  font-weight: 600;
  margin-bottom: 0.15rem;
}

.spec-value {
  color: var(--ink, #2d2d2d);
  font-size: 0.95rem;
}

.spec-turn {
  border-top: 1px solid var(--border-sketch, rgba(0,0,0,0.08));
  padding-top: 1rem;
  margin-top: 0.5rem;
}

.spec-turn .spec-value {
  margin-top: 0.15rem;
}

/* Complete actions */
.complete-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
}

.btn-save {
  font-family: var(--font-heading, 'Fredoka', sans-serif);
  font-size: 1rem;
  padding: 0.7rem 2rem;
  border-radius: 12px;
  border: none;
  background-color: var(--pop-orange, #ff6b35);
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-save:hover:not(:disabled) {
  background-color: var(--pop-orange-light, #ff8c5e);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-restart {
  font-family: var(--font-heading, 'Fredoka', sans-serif);
  font-size: 1rem;
  padding: 0.7rem 2rem;
  border-radius: 12px;
  border: none;
  background-color: var(--bg-sketch, #eee8dd);
  color: var(--ink-light, #5a5a5a);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-restart:hover {
  background-color: var(--pencil-light, #d4c9b5);
}

.save-error {
  color: var(--pop-red, #e84855);
  margin-top: 1rem;
  font-size: 0.9rem;
}

/* Mobile */
@media (max-width: 576px) {
  .question-text {
    font-size: 1.4rem;
  }

  .designer-input {
    font-size: 1.1rem;
  }

  .spec-grid {
    grid-template-columns: 1fr;
  }
}
</style>
